// =============================================================================
// src/views/mapa-eleitoral/playback.ts — Maquina de estado da timeline (play/speed).
//
// Proveniencia: assets/unified-app.js Sections.mapaEleitoral._setYear /
//   _togglePlay / _startPlayInterval (linhas 974-1016). TIMING PRESERVADO:
//   setInterval(1500 / speed) avança ano-a-ano; ao chegar no ultimo ano, um
//   setTimeout(1500 / speed) para o loop e troca o icone para "play". A duracao
//   e congelada no momento da criacao do interval — trocar a velocidade durante o
//   play exige clear + restart (feito pelo binding de speed em index.ts), igual ao
//   original. O indice de avanco e LOCAL ao loop (closure), portanto cliques
//   manuais em markers durante o play NAO redirecionam o auto-advance — desvio do
//   original deliberadamente mantido (ver resumo).
//
// SRP: so o relogio da timeline. Estilos/overlays sao callbacks injetados.
// =============================================================================

import type { ElectoralYear } from '../../app/types';

/** Anos do ciclo, na ordem da timeline (Sections.mapaEleitoral.years). */
const ELECTORAL_TIMELINE_YEARS: readonly ElectoralYear[] = [
  '2014',
  '2016',
  '2018',
  '2020',
  '2022',
  '2024',
];

/** Base de duracao em ms; a cadencia real e BASE / speed (1500/speed do original). */
const PLAYBACK_BASE_INTERVAL_MS = 1500;

/** Efeitos colaterais que o player dispara, injetados para manter o player puro de DOM. */
export interface TimelinePlaybackHooks {
  /** Aplica o ano (estilos do mapa + label/marker/progress). Replica _setYear. */
  onYearAdvance(year: ElectoralYear): void;
  /** Troca o icone do botao de play para o estado "tocando" (pause). */
  onPlayStart(): void;
  /** Troca o icone do botao de play para o estado "parado" (play). */
  onPlayStop(): void;
}

/**
 * Player da timeline historica do Mapa Eleitoral. Mantem isPlaying/speed/ano e o
 * handle do interval; os efeitos visuais vem via TimelinePlaybackHooks.
 */
export class MapaTimelinePlayback {
  isPlaying = false;
  speed = 1;
  currentYear: ElectoralYear = '2024';
  private intervalHandle: ReturnType<typeof setInterval> | null = null;

  constructor(private readonly playbackHooks: TimelinePlaybackHooks) {}

  /** Lista de anos exposta para o binding de markers (indexOf de ano selecionado). */
  get years(): readonly ElectoralYear[] {
    return ELECTORAL_TIMELINE_YEARS;
  }

  /** Define o ano atual e dispara o hook de avanco (replica _setYear sem o DOM). */
  setYear(year: ElectoralYear): void {
    this.currentYear = year;
    this.playbackHooks.onYearAdvance(year);
  }

  /** Alterna play/pause (replica _togglePlay) preservando troca de icone. */
  togglePlay(): void {
    if (this.isPlaying) {
      this.stop();
      return;
    }
    this.isPlaying = true;
    this.playbackHooks.onPlayStart();
    this.startInterval();
  }

  /**
   * Reinicia o interval com a velocidade vigente. Usado pelo binding de speed
   * quando a troca acontece durante o play (clear + restart como no original).
   */
  restartInterval(): void {
    this.clearInterval();
    this.startInterval();
  }

  /** Para o loop e reseta isPlaying/icone (ramo "isPlaying" de _togglePlay + deactivate). */
  stop(): void {
    this.clearInterval();
    this.isPlaying = false;
    this.playbackHooks.onPlayStop();
  }

  /** Para o loop sem mexer no icone (deactivate, que so limpa o interval e a flag). */
  stopSilently(): void {
    if (!this.isPlaying) return;
    this.clearInterval();
    this.isPlaying = false;
  }

  /**
   * Liga o setInterval(1500/speed) que avança ano-a-ano (replica _startPlayInterval).
   * `advanceIndex` e local ao loop — congelado a partir do ano atual no start.
   */
  private startInterval(): void {
    let advanceIndex = ELECTORAL_TIMELINE_YEARS.indexOf(this.currentYear);
    const tickMs = PLAYBACK_BASE_INTERVAL_MS / this.speed;
    this.intervalHandle = setInterval(() => {
      advanceIndex = (advanceIndex + 1) % ELECTORAL_TIMELINE_YEARS.length;
      const advancedYear = ELECTORAL_TIMELINE_YEARS[advanceIndex];
      if (advancedYear !== undefined) this.setYear(advancedYear);
      if (advanceIndex === ELECTORAL_TIMELINE_YEARS.length - 1) this.scheduleStopAtEnd();
    }, tickMs);
  }

  /** Agenda a parada no fim do ciclo: setTimeout(1500/speed) -> clear + icone play. */
  private scheduleStopAtEnd(): void {
    setTimeout(() => {
      this.clearInterval();
      this.isPlaying = false;
      this.playbackHooks.onPlayStop();
    }, PLAYBACK_BASE_INTERVAL_MS / this.speed);
  }

  /** Limpa o handle do interval, se houver (guarda contra clearInterval(null)). */
  private clearInterval(): void {
    if (this.intervalHandle !== null) {
      clearInterval(this.intervalHandle);
      this.intervalHandle = null;
    }
  }
}
